import React, { createContext, useContext, useMemo, useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { translations, defaultLocale, supportedLocales, type Locale, type Translation } from '../i18n'

type I18nValue = {
  locale: Locale
  t: Translation
  setLocale: (l: Locale, opts?: { redirect?: boolean; replace?: boolean }) => void
  detectPreferred: () => Locale
}

const I18nContext = createContext<I18nValue | null>(null)

const LS_KEY = 'ca.locale'

const detectBrowserLocale = (): Locale => {
  const nav = typeof navigator !== 'undefined' ? navigator : undefined
  const lang = nav?.language?.toLowerCase() || nav?.languages?.[0]?.toLowerCase() || ''
  if (lang.startsWith('pt')) return 'pt'
  if (lang.startsWith('en')) return 'en'
  if (lang.startsWith('fr')) return 'fr'
  if (lang.startsWith('es')) return 'es'
  return defaultLocale
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(defaultLocale)
  const location = useLocation()
  const navigate = useNavigate()

  const getUrlLocale = (): Locale | null => {
    const m = location.pathname.match(/^\/(pt|en|fr|es)(?:\/|$)/)
    return (m ? (m[1] as Locale) : null)
  }

  const detectPreferred = (): Locale => {
    const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null
    if (saved && (supportedLocales as string[]).includes(saved)) {
      return saved as Locale
    }
    const urlL = getUrlLocale()
    if (urlL) return urlL
    return detectBrowserLocale()
  }

  useEffect(() => {
    const preferred = detectPreferred()
    setLocaleState(preferred)
  }, [])

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale
    }
  }, [locale])

  const t = useMemo(() => translations[locale], [locale])

  const setLocale = (l: Locale, opts?: { redirect?: boolean; replace?: boolean }) => {
    setLocaleState(l)
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, l)
    } catch {}
    const shouldRedirect = opts?.redirect !== false
    if (shouldRedirect) {
      const rest = location.pathname.replace(/^\/(pt|en|fr|es)/, '') || '/'
      const to = `/${l}${rest.startsWith('/') ? rest : `/${rest}`}${location.search}${location.hash}`
      navigate(to, { replace: opts?.replace === true })
    }
  }

  return (
    <I18nContext.Provider value={{ locale, t, setLocale, detectPreferred }}>
      {children}
    </I18nContext.Provider>
  )
}

export const useI18n = () => {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}
